// apps/client/src/pages/leads/edm/components/TagPicker.tsx
//
// Contact-tag filter for the eDM audience (S2). Tags filter IMPORTED CONTACTS
// only (leads and clients are never tag-filtered). The list comes from
// edmCampaignAction.listTags: registry entries (kind + optional event date)
// plus tags found on contacts but not registered. The list loads when the
// picker is opened, so opening the editor makes no eDM call.

import React, { useMemo, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmTag, EdmTagKind } from '../../../../hooks/useEdmCampaigns';

/** Server zod cap on audience.contactTags. */
export const EDM_MAX_CONTACT_TAGS = 20;

const KIND_LABEL: Record<EdmTagKind, string> = { tag: 'Tag', category: 'Category', event: 'Event' };

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' (string only; no time zone involved). */
export function formatEventDate(d: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

const TagPicker: React.FC<{
  /** null = no tag filter. */
  selected: string[] | null | undefined;
  onChange: (tags: string[] | null) => void;
  listTags: () => Promise<{ tags: EdmTag[] }>;
  disabled?: boolean;
  /**
   * edm_content: whether the tags also filter leads and clients
   * (audience.tagsApplyTo 'all'). Absent = the Phase 1c contacts-only copy.
   */
  appliesToAll?: boolean;
}> = ({ selected, onChange, listTags, disabled, appliesToAll }) => {
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<EdmTag[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const chosen = useMemo(() => selected ?? [], [selected]);
  const byName = useMemo(() => new Map((tags ?? []).map((t) => [t.name, t])), [tags]);
  const atCap = chosen.length >= EDM_MAX_CONTACT_TAGS;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTags();
      setTags(Array.isArray(res?.tags) ? res.tags : []);
    } catch (err) {
      setError(edmErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && tags === null && !loading) void load();
  };

  const set = (names: string[]) => onChange(names.length > 0 ? names : null);
  const toggle = (name: string) => set(chosen.includes(name) ? chosen.filter((x) => x !== name) : [...chosen, name]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = tags ?? [];
    return q ? list.filter((t) => t.name.toLowerCase().includes(q)) : list;
  }, [tags, filter]);

  return (
    <div className="space-y-2" data-testid="edm-tag-picker">
      {appliesToAll === undefined ? (
        <>
          <span className={EDM_UI.label}>Contact tags</span>
          <p className="text-xs text-gray-400">
            Only imported contacts with at least one of these tags. Leads and clients are not filtered by tag. Leave empty
            to include every contact.
          </p>
        </>
      ) : (
        <>
          <span className={EDM_UI.label}>Tags</span>
          <p className="text-xs text-gray-400">
            {appliesToAll
              ? 'Only people with at least one of these tags: imported contacts, leads and clients. Leave empty to include everyone.'
              : 'Only imported contacts with at least one of these tags. Leads and clients are filtered too only when you tick "Apply tags to leads and clients too". Leave empty to include every contact.'}
          </p>
        </>
      )}
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((name) => {
            const t = byName.get(name);
            const date = formatEventDate(t?.eventDate);
            return (
              <span key={name} className={`${EDM_UI.chip} gap-1 border-blue-500/40 bg-blue-500/10 text-blue-200`}>
                <Tag className="h-3 w-3" />
                <span>{name}</span>
                {t && <span className="text-gray-400">{KIND_LABEL[t.kind] ?? t.kind}</span>}
                {date && <span className="text-gray-400">{date}</span>}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggle(name)}
                    className="text-gray-400 hover:text-red-300"
                    aria-label={`Remove tag ${name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {!disabled && (
        <button type="button" className={EDM_UI.secondaryButton} onClick={toggleOpen} aria-expanded={open}>
          <Plus className="h-3.5 w-3.5" /> {open ? 'Hide tags' : 'Choose tags'}
        </button>
      )}
      {open && !disabled && (
        <div className="rounded-md border border-gray-700 bg-gray-900/40">
          {(tags?.length ?? 0) > 8 && (
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search tags"
              aria-label="Search tags"
              className="w-full rounded-t-md border-b border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-100 focus:outline-none"
            />
          )}
          <div className="max-h-48 overflow-y-auto p-1">
            {loading ? (
              <div className="px-2 py-3 text-xs text-gray-500">Loading tags…</div>
            ) : error ? (
              <div className="px-2 py-3 text-xs text-red-300">
                {error}{' '}
                <button type="button" className="underline" onClick={() => void load()}>
                  Retry
                </button>
              </div>
            ) : (tags?.length ?? 0) === 0 ? (
              <div className="px-2 py-3 text-xs text-gray-500">No contact tags yet. Tags come from imports and the Contacts page.</div>
            ) : filtered.length === 0 ? (
              <div className="px-2 py-3 text-xs text-gray-500">No tags match your search.</div>
            ) : (
              filtered.map((t) => {
                const checked = chosen.includes(t.name);
                const date = formatEventDate(t.eventDate);
                return (
                  <label key={t.name} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!checked && atCap}
                      onChange={() => toggle(t.name)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="text-xs text-gray-200">{t.name}</span>
                    <span className={`${EDM_UI.chip} border-gray-600 text-gray-400`}>{KIND_LABEL[t.kind] ?? t.kind}</span>
                    {date && <span className="text-xs text-gray-400">{date}</span>}
                    {t.registered === false && <span className="text-xs text-gray-500">(from contacts)</span>}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
      {atCap && !disabled && <p className="text-xs text-amber-300">You can choose up to {EDM_MAX_CONTACT_TAGS} tags.</p>}
    </div>
  );
};

export default TagPicker;
